<script lang="ts">
export default {
  name: 'UISelect',
};

const selectTriggerVariants = cva(
  'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus:ring-ring data-[state=open]:ring-2 data-[state=open]:ring-ring data-[state=open]:ring-offset-2 flex w-full items-center justify-between rounded-md border border-solid focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      size: {
        default: 'h-10 px-3 py-2 text-sm',
        sm: 'h-8 px-2 py-1 text-xs',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

const selectItemVariants = cva(
  'hover:text-accent-foreground data-highlighted:text-accent-foreground data-[state=checked]:text-accent-foreground/50 relative flex cursor-pointer items-center rounded-sm transition-colors duration-100 outline-none select-none hover:bg-secondary/30 data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-secondary/30 data-[state=checked]:bg-secondary/50',
  {
    variants: {
      size: {
        default: 'px-2 py-1.5 text-sm',
        sm: 'px-2 py-1 text-xs',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

export type SelectVariants = VariantProps<typeof selectTriggerVariants>;
</script>

<script setup lang="ts">
import { computed } from 'vue';
import { ChevronDownIcon } from '@lucide/vue';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectPortal,
  SelectContent,
  SelectViewport,
  SelectItem,
  SelectItemText,
} from 'reka-ui';

interface Option {
  value: string;
  label: string;
  description?: string;
}

interface Props {
  modelValue?: string;
  options: Option[];
  placeholder?: string;
  class?: string;
  disabled?: boolean;
  size?: SelectVariants['size'];
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'Select an option',
  disabled: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const selectedValue = computed({
  get: () => props.modelValue || 'empty',
  set: (value) => emit('update:modelValue', value === 'empty' ? '' : value as string),
});

const selectedOption = computed(() => {
  return props.options.find(option => option.value === props.modelValue);
});
</script>

<template>
  <SelectRoot v-model="selectedValue" :disabled="disabled">
    <SelectTrigger
      :class="cn(selectTriggerVariants({ size: props.size }), props.class)">
      <SelectValue :placeholder="placeholder">
        <template v-if="selectedOption">
          <div class="flex gap-1 items-center">
            <div>{{ selectedOption.label }}</div>
            <div class="text-xs italic" >{{ selectedOption.description }}</div>
          </div>
        </template>
      </SelectValue>
      <SelectIcon as-child>
        <ChevronDownIcon class="data-[state=open]:rotate-180 h-4 w-4 opacity-50 transition-transform" />
      </SelectIcon>
    </SelectTrigger>
    <SelectPortal>
      <SelectContent
        class="border-input bg-popover text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-9999 min-w-32 overflow-hidden rounded-md border bg-base-100 shadow-md"
        :side-offset="5">
        <SelectViewport class="p-1">
          <SelectItem
            v-for="option in options"
            :key="option.value"
            :value="option.value || 'empty'"
            :class="selectItemVariants({ size: props.size })">
            <SelectItemText>
              <div>
                <div class="font-medium">{{ option.label }}</div>
                <div
                  v-if="option.description"
                  :class="['text-muted-foreground', props.size === 'sm' ? 'text-[10px]' : 'text-xs']">
                  {{ option.description }}
                </div>
              </div>
            </SelectItemText>
          </SelectItem>
        </SelectViewport>
      </SelectContent>
    </SelectPortal>
  </SelectRoot>
</template>
